// Single JavaScript file - All functionality in one place
// No imports - GSAP loaded globally via CDN

// Marquee class
class Marquee {
  constructor(el) {
    this.el = el;
    // If the element itself has the marquee-inner class, use it directly
    // Otherwise look for a child with marquee-inner class
    this.slides = el.classList.contains('e-marquee-inner') ? el : el.querySelector('.e-marquee-inner');
    if (!this.slides) {
      console.warn('Marquee: Could not find .e-marquee-inner element');
      return;
    }
    this.speed = parseFloat(el.getAttribute('data-speed')) || 10; // seconds for full cycle
    this.cloneCount = 3;
    this.direction = el.getAttribute('data-direction') || 'left';
    this.init();
  }

  init() {
    if (!this.slides) return;
    
    // Clone the children cloneCount times for seamless loop
    const originalChildren = Array.from(this.slides.children);
    for (let i = 0; i < this.cloneCount; i++) {
      originalChildren.forEach(child => {
        this.slides.appendChild(child.cloneNode(true));
      });
    }

    // Calculate width
    const width = this.slides.scrollWidth / (this.cloneCount + 1);

    // GSAP animation based on direction
    const targetX = this.direction === 'right' ? width : -width;
    gsap.set(this.slides, { x: 0 });
    this.tl = gsap.to(this.slides, {
      x: targetX,
      duration: this.speed,
      ease: 'none',
      repeat: -1,
    });

    // No hover events - marquee runs continuously
  }
}

class MenuDrawer {
  constructor() {
    this.drawer = document.querySelector('.e-menu-drawer');
    this.openButtons = document.querySelectorAll('.e-open-menu-drawer');
    if (!this.drawer || !this.openButtons.length) return;

    this.closeBtn = this.drawer.querySelector('.e-menu-drawer-close');
    this.overlay = this.drawer.querySelector('.e-menu-drawer-overlay');
    this.focusableSelectors = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this.firstFocusable = null;
    this.lastFocusable = null;
    this.handleKeyDown = this.onKeyDown.bind(this);

    this.init();
  }

  init() {
    this.openButtons.forEach(btn => btn.addEventListener('click', () => this.open()));
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    const links = this.drawer.querySelectorAll('.e-menu-drawer-list a');
    links.forEach(link => link.addEventListener('click', () => this.close()));

    this.drawer.setAttribute('aria-hidden', 'true');
  }

  cacheFocusable() {
    const focusables = Array.from(this.drawer.querySelectorAll(this.focusableSelectors));
    this.firstFocusable = focusables[0] || null;
    this.lastFocusable = focusables[focusables.length - 1] || this.firstFocusable;
  }

  open() {
    if (this.drawer.classList.contains('active')) return;
    this.cacheFocusable();
    this.drawer.classList.add('active');
    document.body.classList.add('is-menu-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.firstFocusable?.focus();
    document.addEventListener('keydown', this.handleKeyDown);
  }

  close() {
    if (!this.drawer.classList.contains('active')) return;
    this.drawer.classList.remove('active');
    document.body.classList.remove('is-menu-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  onKeyDown(event) {
    /** @type {KeyboardEvent} */
    const keyEvent = event;
    if (keyEvent.key === 'Escape') {
      keyEvent.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.firstFocusable) return;

    if (event.shiftKey && document.activeElement === this.firstFocusable) {
      event.preventDefault();
      this.lastFocusable?.focus();
    } else if (!event.shiftKey && document.activeElement === this.lastFocusable) {
      event.preventDefault();
      this.firstFocusable?.focus();
    }
  }
}

// Countdown Timer class
class CountdownTimer {
  constructor(el) {
    this.el = el;
    this.daysEl = el.querySelector('[data-unit="days"] .e-digit');
    this.hoursEl = el.querySelector('[data-unit="hours"] .e-digit');
    this.minsEl = el.querySelector('[data-unit="minutes"] .e-digit');
    this.secsEl = el.querySelector('[data-unit="seconds"] .e-digit');
    this.timeLeft = (6*86400) + (4*3600) + (34*60) + 56; // 6 days + 4 hrs + 34 mins + 56 secs
    this.intervalId = null;
    this.digits = el.querySelectorAll('.e-digit');
    this.lastValues = { days: -1, hours: -1, minutes: -1, seconds: -1 };
  }

  init() {
    this.update();
    this.intervalId = window.setInterval(() => {
      if (this.timeLeft <= 0) {
        this.stop();
        this.showEnded();
        return;
      }

      this.timeLeft--;
      this.update();
      this.animateDigits();
    }, 1000);
  }

  update() {
    const days = Math.floor(this.timeLeft / 86400);
    const hours = Math.floor((this.timeLeft % 86400) / 3600);
    const minutes = Math.floor((this.timeLeft % 3600) / 60);
    const seconds = this.timeLeft % 60;

    // Only update if value changed
    if (days !== this.lastValues.days && this.daysEl) {
      this.daysEl.textContent = days.toString().padStart(2, '0');
      this.lastValues.days = days;
    }
    if (hours !== this.lastValues.hours && this.hoursEl) {
      this.hoursEl.textContent = hours.toString().padStart(2, '0');
      this.lastValues.hours = hours;
    }
    if (minutes !== this.lastValues.minutes && this.minsEl) {
      this.minsEl.textContent = minutes.toString().padStart(2, '0');
      this.lastValues.minutes = minutes;
    }
    if (seconds !== this.lastValues.seconds && this.secsEl) {
      this.secsEl.textContent = seconds.toString().padStart(2, '0');
      this.lastValues.seconds = seconds;
    }
  }

  animateDigits() {
    if (typeof gsap === 'undefined' || !this.digits.length) return;
    gsap.to(this.digits, {
      scale: 1.1,
      duration: 0.15,
      yoyo: true,
      repeat: 1,
      ease: 'power1.out'
    });
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  showEnded() {
    if (this.el.querySelector('.countdown-ended')) return;
    const endedMessage = document.createElement('div');
    endedMessage.className = 'countdown-ended text-2xl font-bold text-red-400 text-center mt-4';
    endedMessage.textContent = 'OFFER ENDED';
    this.el.appendChild(endedMessage);
  }
}

// Hero entrance animation
function initHeroAnimations() {
  // Skip hero animations since hero classes don't exist in current HTML
  console.log('Hero animations skipped - no hero elements found');
}

// Smooth scrolling for navigation links
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });
}

// Initialize Swiper testimonials carousel
function initTestimonialsSwiper() {
  if (typeof Swiper === 'undefined') return;
  const swiperEl = document.querySelector('.swiper.testimonials-swiper');
  if (!swiperEl) return;

  const swiper = new Swiper('.swiper.testimonials-swiper', {
    slidesPerView: 1,
    spaceBetween: 30,
    speed: 700,
    loop: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },
    breakpoints: {
      768: { slidesPerView: 1.5 },
      1024: { slidesPerView: 2.2 },
      1280: { slidesPerView: 3 },
    },
  });

  const prevBtn = document.querySelector('.testimonial-prev');
  const nextBtn = document.querySelector('.testimonial-next');
  prevBtn?.addEventListener('click', () => swiper.slidePrev());
  nextBtn?.addEventListener('click', () => swiper.slideNext());
}

// Hero Swiper slider with vertical pagination
function initHeroSwiper() {
  if (typeof Swiper === 'undefined') {
    console.log('Swiper not loaded yet');
    return;
  }
  const heroSwiperEl = document.querySelector('.e-section-hero-visual .swiper.e-hero-swiper');
  if (!heroSwiperEl) {
    console.log('Hero swiper element not found');
    return;
  }

  const paginationEl = document.querySelector('.e-section-hero-pagination');

  new Swiper(heroSwiperEl, {
    loop: true,
    speed: 800,
    effect: 'fade',
    fadeEffect: {
      crossFade: true,
    },
    autoplay: {
      delay: 4500,
      disableOnInteraction: false,
    },
    pagination: {
      el: paginationEl,
      clickable: true,
      bulletClass: 'swiper-pagination-bullet',
      bulletActiveClass: 'swiper-pagination-bullet-active',
    },
  });
}

// Initialize all countdown timers
function initCountdownTimers() {
  const countdownEls = document.querySelectorAll('[data-countdown]');
  if (!countdownEls.length) return;

  countdownEls.forEach(el => {
    const timer = new CountdownTimer(el);
    timer.init();
  });
}

// Products Swiper with two rows
function initProductsSwiper() {
  if (typeof Swiper === 'undefined') return;
  const productsSwiper = document.querySelector('.swiper.e-products-swiper');
  if (!productsSwiper) return;

  new Swiper('.swiper.e-products-swiper', {
    slidesPerView: 1.1,
    spaceBetween: 30,
    grid: {
      rows: 1,
      fill: 'row',
    },
    navigation: {
      nextEl: '.products-next',
      prevEl: '.products-prev',
    },
    breakpoints: {
      640: {
        slidesPerView: 1.6,
        spaceBetween: 24,
        grid: {
          rows: 1,
        },
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 24,
        grid: {
          rows: 2,
        },
      },
      1200: {
        slidesPerView: 3,
        spaceBetween: 30,
        grid: {
          rows: 2,
        },
      },
    },
  });
}

// Motion One helper for reveal animations
function initMotionAnimations() {
  const Motion = window.Motion;
  if (!Motion || !Motion.animate) return;

  const animatedEls = document.querySelectorAll('[data-animate]');
  if (!animatedEls.length) return;

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const type = el.dataset.animate || 'fade-up';
      const delay = parseFloat(el.dataset.animateDelay) || 0;

      const keyframes = type === 'fade-up'
        ? { opacity: [0, 1], transform: ['translateY(30px)', 'translateY(0)'] }
        : { opacity: [0, 1] };

      Motion.animate(el, keyframes, {
        duration: 0.8,
        delay,
        easing: 'ease-out',
        fill: 'forwards'
      });

      obs.unobserve(el);
    });
  }, { threshold: 0.2 });

  animatedEls.forEach(el => {
    el.style.opacity = 0;
    observer.observe(el);
  });
}

// Video lightbox with Vimeo autoplay
function initVideoLightbox() {
  const triggers = document.querySelectorAll('[data-video-trigger]');
  const lightbox = document.querySelector('.e-video-lightbox');
  if (!triggers.length || !lightbox) return;

  const iframe = lightbox.querySelector('iframe');
  const closeBtn = lightbox.querySelector('[data-video-close]');

  const openLightbox = (videoId) => {
    const src = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=0&title=0&byline=0&portrait=0`;
    iframe.src = src;
    lightbox.classList.add('active');
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    lightbox.classList.remove('active');
    iframe.src = '';
    document.body.style.overflow = '';
  };

  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const videoId = trigger.dataset.videoId;
      if (videoId) openLightbox(videoId);
    });
  });

  closeBtn?.addEventListener('click', closeLightbox);
  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox) closeLightbox();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('active')) closeLightbox();
  });
}

// Add to Cart handler for product cards
function initAddToCart() {
  document.querySelectorAll('.btn--add-to-cart[data-add-to-cart]').forEach(btn => {
    btn.addEventListener('click', async function (e) {
      e.preventDefault();
      e.stopPropagation();

      const variantId = this.dataset.variantId;
      if (!variantId) return;

      const btnText = this.querySelector('span');
      const originalText = btnText ? btnText.textContent : '';

      // Disable button and show loading state
      this.disabled = true;
      this.classList.add('is-loading');
      if (btnText) btnText.textContent = 'Adding...';

      try {
        const response = await fetch(
          (window.Theme && Theme.routes.cart_add_url) || '/cart/add.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              id: parseInt(variantId, 10),
              quantity: 1,
            }),
          }
        );

        const data = await response.json();

        if (response.ok) {
          // Show success state
          this.classList.remove('is-loading');
          this.classList.add('is-added');
          if (btnText) btnText.textContent = 'Added!';

          // Update cart count in header
          updateCartCount();

          // Reset button after 2 seconds
          setTimeout(() => {
            this.classList.remove('is-added');
            if (btnText) btnText.textContent = originalText;
            this.disabled = false;
          }, 2000);
        } else {
          // Show error
          this.classList.remove('is-loading');
          if (btnText) btnText.textContent = data.description || 'Error';
          setTimeout(() => {
            if (btnText) btnText.textContent = originalText;
            this.disabled = false;
          }, 2000);
        }
      } catch (err) {
        console.error('Add to cart error:', err);
        this.classList.remove('is-loading');
        if (btnText) btnText.textContent = originalText;
        this.disabled = false;
      }
    });
  });
}

// Fetch current cart and update the header cart count badge
async function updateCartCount() {
  try {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    const cart = await res.json();
    const countEls = document.querySelectorAll(
      '.cart-count, .cart-count-bubble span, [data-cart-count], .header-actions__cart-count'
    );
    countEls.forEach(el => {
      el.textContent = cart.item_count;
      if (cart.item_count > 0) el.removeAttribute('hidden');
    });
  } catch (e) {
    // silently fail
  }
}

// Update wishlist counter in header
function updateWishlistCounter() {
  const wishlist = JSON.parse(localStorage.getItem('e-wishlist') || '[]');
  const count = wishlist.length;
  const counterEls = document.querySelectorAll('[data-wishlist-counter]');
  
  counterEls.forEach(el => {
    el.textContent = count;
    if (count > 0) {
      el.style.display = 'flex'; // Show counter
    } else {
      el.style.display = 'none'; // Hide counter when empty
    }
  });
  
  console.log('Updated wishlist counter:', count);
}

// Wishlist functionality using localStorage with full product data
function initWishlist() {
  console.log('Initializing wishlist...');

  // Get wishlist from localStorage
  function getWishlist() {
    const wishlist = localStorage.getItem('e-wishlist');
    const parsed = wishlist ? JSON.parse(wishlist) : [];
    console.log('Current wishlist:', parsed);
    
    // Migrate old format (array of IDs) to new format (array of objects)
    const needsMigration = parsed.length > 0 && typeof parsed[0] === 'string';
    if (needsMigration) {
      console.log('Migrating old wishlist format:', parsed);
      const migrated = parsed.map(id => ({
        id: id.toString(),
        title: 'Product ' + id,
        vendor: 'Brand',
        url: '#',
        featured_image: null,
        price: '$0.00',
        compare_at_price: null,
        selected_or_first_available_variant: {
          id: id.toString()
        }
      }));
      saveWishlist(migrated);
      console.log('Migrated wishlist:', migrated);
      return migrated;
    }
    
    return parsed;
  }

  // Save wishlist to localStorage
  /**
   * @param {Array<{id: string, title: string, vendor: string, url: string, featured_image?: {src: string, alt?: string}, price: string, compare_at_price?: string, selected_or_first_available_variant?: {id: string}}>} wishlist
   */
  function saveWishlist(wishlist) {
    localStorage.setItem('e-wishlist', JSON.stringify(wishlist));
    console.log('Saved wishlist:', wishlist);
    updateWishlistCounter();
  }

  // Check if product is in wishlist
  /**
   * @param {string|number} productId - The product ID to check
   * @returns {boolean} - Whether the product is in the wishlist
   */
  function isInWishlist(productId) {
    const wishlist = getWishlist();
    const result = wishlist.some(/** @param {{id: string}} item */ item => item.id === productId.toString());
    console.log('Is product', productId, 'in wishlist?', result);
    return result;
  }

  // Add product to wishlist with full data
  /**
   * @param {{id: string, title: string, vendor: string, url: string, featured_image?: {src: string, alt?: string}, price: string, compare_at_price?: string, rating?: string, reviews?: string, variant_id?: string}} productData
   */
  function addToWishlist(productData) {
    let wishlist = getWishlist();
    
    // Check if already in wishlist
    if (wishlist.some(item => item.id === productData.id.toString())) {
      console.log('Product already in wishlist');
      return false;
    }

    // Add product data
    wishlist.push({
      id: productData.id.toString(),
      title: productData.title || 'Unknown Product',
      vendor: productData.vendor || 'Brand',
      url: productData.url || '#',
      featured_image: productData.featured_image || null,
      price: productData.price || '0.00',
      compare_at_price: productData.compare_at_price || null,
      rating: productData.rating || '★★★★★',
      reviews: productData.reviews || '20',
      variant_id: productData.variant_id || productData.id.toString()
    });

    saveWishlist(wishlist);
    console.log('Added to wishlist:', productData.title);
    return true;
  }

  // Remove product from wishlist
  function removeFromWishlist(productId) {
    let wishlist = getWishlist();
    const initialLength = wishlist.length;
    wishlist = wishlist.filter(/** @param {{id: string}} item */ item => item.id !== productId.toString());
    
    if (wishlist.length < initialLength) {
      saveWishlist(wishlist);
      console.log('Removed product from wishlist:', productId);
      return true;
    }
    return false;
  }

  // Toggle wishlist (for backward compatibility)
  function toggleWishlist(productId) {
    if (isInWishlist(productId)) {
      removeFromWishlist(productId);
      return false; // Now not in wishlist
    } else {
      // For toggle, we need product data - this will be called from product cards
      console.log('Toggle add - need product data');
      return false;
    }
  }

  // Update button visual state
  function updateWishlistButton(button, productId) {
    const isActive = isInWishlist(productId);
    console.log('Updating button for product', productId, 'to', isActive ? 'active' : 'inactive');
    button.classList.toggle('is-active', isActive);

    // Update aria-label for accessibility
    button.setAttribute('aria-label', isActive ? 'Remove from Wishlist' : 'Add to Wishlist');
  }

  // Handle wishlist button clicks
  const buttons = document.querySelectorAll('[data-wishlist-add]');
  console.log('Found', buttons.length, 'wishlist buttons');

  buttons.forEach(btn => {
    const productId = btn.dataset.productId;
    console.log('Button product ID:', productId);
    if (!productId) return;

    // Set initial state
    updateWishlistButton(btn, productId);

    // Add click handler
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();

      console.log('Adding product to wishlist:', productId);
      
      // Get product data from the DOM
      const productCard = this.closest('.product-card');
      let productData = { id: productId };
      
      if (productCard) {
        const titleEl = productCard.querySelector('.product-title a, .product-title');
        const vendorEl = productCard.querySelector('.product-brand');
        const priceEl = productCard.querySelector('.product-price');
        const comparePriceEl = productCard.querySelector('.product-price__compare');
        const imgEl = productCard.querySelector('.product-media img');
        const ratingEl = productCard.querySelector('.product__rating-star');
        const reviewsEl = productCard.querySelector('.product__rating-number');
        const variantEl = this.closest('[data-variant-id]') || productCard.querySelector('[data-variant-id]');
        
        // Extract all product information
        productData = {
          id: productId.toString(),
          title: titleEl ? (titleEl.textContent || titleEl.innerText || '').trim() : 'Unknown Product',
          vendor: vendorEl ? (vendorEl.textContent || vendorEl.innerText || '').trim() : 'Brand',
          url: titleEl && titleEl.href ? titleEl.href : '#',
          price: priceEl ? (priceEl.textContent || priceEl.innerText || '').trim().replace('$', '') : '0.00',
          compare_at_price: comparePriceEl ? (comparePriceEl.textContent || comparePriceEl.innerText || '').trim().replace('$', '') : null,
          featured_image: imgEl ? {
            src: imgEl.src || imgEl.getAttribute('src') || '',
            alt: imgEl.alt || imgEl.getAttribute('alt') || ''
          } : null,
          rating: ratingEl ? (ratingEl.textContent || ratingEl.innerText || '').trim() : '★★★★★',
          reviews: reviewsEl ? (reviewsEl.textContent || reviewsEl.innerText || '').replace(/[()]/g, '').replace('reviews', '').trim() : '20',
          variant_id: variantEl ? variantEl.dataset.variantId : productId.toString()
        };
        
        console.log('Extracted product data:', productData);
      } else {
        // Fallback data
        productData = {
          id: productId.toString(),
          title: 'Product ' + productId,
          vendor: 'Brand',
          url: '#',
          price: '0.00',
          compare_at_price: null,
          featured_image: null,
          rating: '★★★★★',
          reviews: '20',
          variant_id: productId.toString()
        };
        console.log('Using fallback product data:', productData);
      }

      if (isInWishlist(productId)) {
        removeFromWishlist(productId);
        updateWishlistButton(this, productId);
      } else {
        addToWishlist(productData);
        updateWishlistButton(this, productId);
      }

      // Visual feedback
      this.style.transform = 'scale(1.1)';
      setTimeout(() => {
        this.style.transform = '';
      }, 150);
    });
  });

  // Initialize counter display
  updateWishlistCounter();
}

async function renderWishlist() {
  console.log('=== RENDER WISHLIST ===');
  const embedDiv = document.getElementById('embeed-e-wishlist-data');
  
  if (!embedDiv) {
    console.log('No wishlist embed div found');
    return;
  }

  const wishlist = JSON.parse(localStorage.getItem('e-wishlist') || '[]');
  console.log("Wishlist items:", wishlist.length);
  
  if (!wishlist.length) {
    embedDiv.innerHTML = '<p class="wishlist-empty">Your wishlist is empty</p>';
    return;
  }

  // Render wishlist with stored data
  const productsHTML = wishlist.map((product, index) => {
    console.log(`Rendering: ${product.title}`);
    
    const featuredImage = product.featured_image ?
      `<img src="${product.featured_image.src}" alt="${product.featured_image.alt || product.title}" loading="lazy">` :
      '<div class="product-media__placeholder">No image</div>';

    const priceDisplay = product.compare_at_price && product.compare_at_price > product.price ?
      `<span class="product-price__sale">$${product.price}</span>
       <span class="product-price__compare">$${product.compare_at_price}</span>` :
      `$${product.price}`;

    return `
      <article class="product-card wishlist-item">
        <div class="product-media">
          ${featuredImage}

          <button class="wishlist-button is-active"
                  aria-label="Remove from Wishlist"
                  data-wishlist-add
                  data-product-id="${product.id}">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 21.65C11.69 21.65 11.39 21.61 11.14 21.52C7.32 20.21 1.25 15.56 1.25 8.68998C1.25 5.18998 4.08 2.34998 7.56 2.34998C9.25 2.34998 10.83 3.00998 12 4.18998C13.17 3.00998 14.75 2.34998 16.44 2.34998C19.92 2.34998 22.75 5.19998 22.75 8.68998C22.75 15.57 16.68 20.21 12.86 21.52C12.61 21.61 12.31 21.65 12 21.65ZM7.56 3.84998C4.91 3.84998 2.75 6.01998 2.75 8.68998C2.75 15.52 9.32 19.32 11.63 20.11C11.81 20.17 12.2 20.17 12.38 20.11C14.68 19.32 21.26 15.53 21.26 8.68998C21.26 6.01998 19.1 3.84998 16.45 3.84998C14.93 3.84998 13.52 4.55998 12.61 5.78998C12.33 6.16998 11.69 6.16998 11.41 5.78998C10.48 4.54998 9.08 3.84998 7.56 3.84998Z" fill="#ff0000"/>
            </svg>
          </button>

          <button class="btn--primary btn--add-to-cart"
                  aria-label="Add to Cart"
                  data-add-to-cart
                  data-product-id="${product.id}"
                  data-variant-id="${product.variant_id || ''}">
            <span>Add to Cart</span>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="11" fill="white"/>
              <g clip-path="url(#clip0_${product.id})">
                <path d="M11.0687 7.90625L14.1625 11M14.1625 11L11.0687 14.0937M14.1625 11H6.73749" stroke="#7EC035" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
            </svg>
          </button>
        </div>

        <div class="product-body">
          <div class="product-body__heading">
            <p class="product-brand">${product.vendor}</p>
            <div class="product__rating">
              ${product.rating || '★★★★★'}
              <p class="product__rating-number">(${product.reviews || '20'} reviews)</p>
            </div>
          </div>
          <h3 class="product-title">
            <a href="${product.url}">${product.title}</a>
          </h3>
          <p class="product-price">
            ${priceDisplay}
          </p>
        </div>
      </article>
    `;
  }).join('');

  embedDiv.innerHTML = `<div class="wishlist-grid">${productsHTML}</div>`;
  console.log('Wishlist rendered successfully');

  // Re-initialize wishlist buttons for rendered products
  setTimeout(() => {
    initWishlist();
    initAddToCart();
  }, 100);
}

// DOMContentLoaded handler - single entry point
document.addEventListener('DOMContentLoaded', () => {
  // Wait a bit for everything to load
  setTimeout(() => {
    // Initialize marquee
    const marqueeEl = document.querySelector('[data-marquee]');
    if (marqueeEl) new Marquee(marqueeEl).init();

    // Initialize menu drawer
    new MenuDrawer();

    // Initialize hero entrance animations (skipped - no hero elements)
    initHeroAnimations();

    // Initialize hero swiper
    initHeroSwiper();

    // Initialize countdown timers
    initCountdownTimers();

    // Initialize smooth scrolling
    initSmoothScroll();

    // Init testimonials carousel + animations
    initTestimonialsSwiper();
    initProductsSwiper();
    initMotionAnimations();
    initVideoLightbox();
    initAddToCart();
    initWishlist();
    renderWishlist();
  }, 100); // Small delay to ensure everything is loaded
});
